import Swiper from 'swiper';
import { A11y, Keyboard, Navigation, Pagination } from 'swiper/modules';

// Lights the "In this guide" entry of the section being read.
function initToc(nav: HTMLElement): void {
	const links = Array.from(nav.querySelectorAll<HTMLAnchorElement>('a[href^="#"]'));
	const targets = links
		.map((link) => document.getElementById(decodeURIComponent(link.hash.slice(1))))
		.filter((target): target is HTMLElement => !!target);

	if (!targets.length) {
		return;
	}

	const list = nav.querySelector<HTMLElement>('.armada-article__toc-list');
	const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
	let frame = 0;
	let current = -1;

	// A guide that scrolls inside its card keeps the lit entry in the middle of it.
	const reveal = (link: HTMLElement): void => {
		if (!list || list.scrollHeight <= list.clientHeight) {
			return;
		}

		const item = link.parentElement ?? link;

		list.scrollTo({
			top: item.offsetTop - (list.clientHeight - item.offsetHeight) / 2,
			behavior: motion.matches ? 'auto' : 'smooth',
		});
	};

	const update = (): void => {
		frame = 0;

		// The heading that last passed a line a third of the way down the screen.
		const line = window.innerHeight / 3;
		let active = 0;

		targets.forEach((target, index) => {
			if (target.getBoundingClientRect().top <= line) {
				active = index;
			}
		});

		links.forEach((link, index) => {
			const on = index === active;
			link.classList.toggle('is-active', on);

			if (on) {
				link.setAttribute('aria-current', 'true');
			} else {
				link.removeAttribute('aria-current');
			}
		});

		if (active !== current) {
			current = active;
			reveal(links[active]);
		}
	};

	window.addEventListener(
		'scroll',
		() => {
			if (!frame) {
				frame = requestAnimationFrame(update);
			}
		},
		{ passive: true }
	);
	update();
}

// The Clipboard API exists only on https; plain http (local sites) needs the old way.
async function copyText(text: string): Promise<boolean> {
	if (navigator.clipboard && window.isSecureContext) {
		try {
			await navigator.clipboard.writeText(text);

			return true;
		} catch {
			// Falls through to the textarea below.
		}
	}

	const area = document.createElement('textarea');
	area.value = text;
	area.setAttribute('readonly', '');
	area.style.cssText = 'position:fixed;top:0;left:0;opacity:0;pointer-events:none';
	document.body.appendChild(area);
	area.select();

	let copied = false;

	try {
		copied = document.execCommand('copy');
	} catch {
		copied = false;
	}

	area.remove();

	return copied;
}

function initCopy(button: HTMLButtonElement): void {
	const tip = button.querySelector<HTMLElement>('.armada-article__share-tip');
	let timer = 0;

	button.addEventListener('click', async () => {
		if (!(await copyText(button.dataset.armadaCopy || window.location.href))) {
			return;
		}

		// The status text is what screen readers announce, so it is set only when copied.
		if (tip) {
			tip.textContent = button.dataset.copied || 'Link copied';
		}

		button.classList.add('is-copied');
		window.clearTimeout(timer);
		timer = window.setTimeout(() => {
			button.classList.remove('is-copied');

			// Cleared once faded out, so the next copy is announced again.
			timer = window.setTimeout(() => {
				if (tip) {
					tip.textContent = '';
				}
			}, 300);
		}, 1800);
	});
}

function initGallery(root: HTMLElement): void {
	const block = root.closest<HTMLElement>('.armada-article-gallery');

	new Swiper(root, {
		modules: [A11y, Keyboard, Navigation, Pagination],
		wrapperClass: 'swiper-wrapper',
		slidesPerView: 1,
		spaceBetween: 16,
		keyboard: { enabled: true, onlyInViewport: true },
		navigation: {
			prevEl: root.querySelector<HTMLElement>('.armada-article-gallery__arrow--prev'),
			nextEl: root.querySelector<HTMLElement>('.armada-article-gallery__arrow--next'),
		},
		pagination: {
			el: block?.querySelector<HTMLElement>('.armada-article-gallery__dots'),
			clickable: true,
			bulletClass: 'armada-dot',
			bulletActiveClass: 'is-active',
		},
	});
}

export default function initArticle(): void {
	document.querySelectorAll<HTMLElement>('[data-armada-toc]').forEach(initToc);
	document.querySelectorAll<HTMLButtonElement>('[data-armada-copy]').forEach(initCopy);
	document.querySelectorAll<HTMLElement>('[data-armada-article-gallery]').forEach(initGallery);
}
